/**
 * My farm — a farmer's read-only view of their OWN farm profile (web port).
 *
 * D-021 Path A. Web counterpart of apps/mobile/app/my-farm.tsx. Scopes to the
 * signed-in user: GET /me/farm-profile is guarded by JwtAuthGuard and resolves
 * `farmer_profiles WHERE user_id = sub`, so there is no user-id param — the
 * bearer token the fseAxios interceptor attaches identifies the row. Read-only;
 * editing is a separate slice (_app.my-farm.edit.tsx).
 *
 * Null handling mirrors the data a G1 self-signup captures: GPS and capacity
 * may be unset ("Not captured"); an independent farmer has no cooperative
 * ("Independent"). The endpoint 404s when the caller has no farmer_profile row
 * (e.g. a coop_admin/admin who is not themselves a farmer) — surfaced as a
 * friendly card rather than the generic error state.
 *
 * Contract verified against services/api/src/me/dto.ts via the manual client
 * src/api/manual/me-farm.ts (validate-API-before-smoke rule).
 */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  ListItem,
  Stack,
  Text,
} from '@fse/ui';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { FseApiError } from '../api/client';
import { meControllerGetFarmProfile } from '../api/generated/me/me';
import type { MyFarmProfileDto, MyFarmProfileDtoPrincipalType } from '../api/generated/models';
import { groupDigits } from '../lib/format';
import { normalizeRegion } from '../lib/regions';

// i18n keys for the TD-057 principal_type discriminator (resolved via t()).
const PRINCIPAL_TYPE_LABEL_KEYS: Record<MyFarmProfileDtoPrincipalType, string> = {
  individual: 'myFarm.principalType.individual',
  informal_group: 'myFarm.principalType.informalGroup',
  formal_coop: 'myFarm.principalType.formalCoop',
};

function principalTypeLabel(principalType: string, t: TFunction): string {
  const key = PRINCIPAL_TYPE_LABEL_KEYS[principalType as MyFarmProfileDtoPrincipalType];
  // Unknown discriminator — fall back to the raw value rather than hide it.
  return key ? t(key) : principalType;
}

/** Format a {lat, lng} pair to a fixed-precision string, or null. */
function formatGps(gps: MyFarmProfileDto['farmGps']): string | null {
  if (!gps) return null;
  return `${gps.lat.toFixed(5)}, ${gps.lng.toFixed(5)}`;
}

/** A farm-detail row rendered as a kit `ListItem`: muted label as the title,
 *  the value right-aligned as a trailing `Text`. `muted` de-emphasises
 *  "Not captured" / "Independent". `noDivider` drops the last row's rule. */
function DetailRow({
  label,
  value,
  muted = false,
  leadingIcon,
  noDivider = false,
}: {
  label: string;
  value: ReactNode;
  muted?: boolean;
  leadingIcon?: string;
  noDivider?: boolean;
}) {
  return (
    <ListItem
      leadingIcon={leadingIcon}
      title={
        <Text variant="body-medium" color="text.primary">
          {label}
        </Text>
      }
      trailing={
        <Box sx={{ textAlign: 'right' }}>
          <Text
            variant="body-medium"
            color={muted ? 'text.secondary' : 'text.primary'}
          >
            <Box
              component="span"
              sx={{
                fontWeight: muted ? 400 : 600,
                fontStyle: muted ? 'italic' : 'normal',
              }}
            >
              {value}
            </Box>
          </Text>
        </Box>
      }
      noDivider={noDivider}
    />
  );
}

export function MyFarmScreen() {
  const { t } = useTranslation();
  const farmQuery = useQuery({
    queryKey: ['me-farm-profile'],
    queryFn: ({ signal }) => meControllerGetFarmProfile(signal),
    // A 404 (non-farmer account) is an expected terminal state, not a flaky
    // failure — don't burn retries on it.
    retry: (failureCount, error) =>
      error instanceof FseApiError && error.status === 404 ? false : failureCount < 3,
  });

  const farm = farmQuery.data;
  const isNotFarmer = farmQuery.error instanceof FseApiError && farmQuery.error.status === 404;
  // Region (geo-region MVP) — read DEFENSIVELY off the not-yet-regenerated DTO.
  const region = farm ? normalizeRegion((farm as { region?: string | null }).region) : null;

  return (
    <Stack spacing={3} sx={{ maxWidth: 640 }}>
      <Box>
        <Text variant="headline-small" as="h2">
          {t('myFarm.title')}
        </Text>
        <Box sx={{ mt: 0.5 }}>
          <Text variant="body-medium" color="text.secondary">
            {t('myFarm.subtitle')}
          </Text>
        </Box>
      </Box>

      {farmQuery.isPending ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : isNotFarmer ? (
        <Card>
          <CardHeader title={t('myFarm.notFarmer.title')} />
          <CardContent>
            <Text variant="body-medium" color="text.secondary">
              {t('myFarm.notFarmer.body')}
            </Text>
          </CardContent>
        </Card>
      ) : farmQuery.isError || !farm ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => farmQuery.refetch()}>
              {t('myFarm.retry')}
            </Button>
          }
        >
          {t('myFarm.loadError', { error: String(farmQuery.error) })}
        </Alert>
      ) : (
        <>
          <Card>
            <CardHeader title={t('myFarm.farmDetails')} />
            <CardContent>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  overflow: 'hidden',
                }}
              >
                <DetailRow
                  leadingIcon="park"
                  label={t('myFarm.treeCount')}
                  value={groupDigits(String(farm.treeCount))}
                />
                <DetailRow
                  leadingIcon="scale"
                  label={t('myFarm.estCapacity')}
                  value={farm.estCapacityKg == null ? t('myFarm.notCaptured') : groupDigits(String(farm.estCapacityKg))}
                  muted={farm.estCapacityKg == null}
                />
                <DetailRow
                  leadingIcon="my_location"
                  label={t('myFarm.farmGps')}
                  value={formatGps(farm.farmGps) ?? t('myFarm.notCaptured')}
                  muted={farm.farmGps == null}
                />
                <DetailRow
                  leadingIcon="pin"
                  label={t('myFarm.region', 'Region')}
                  value={region ?? t('myFarm.notCaptured')}
                  muted={region == null}
                />
                <DetailRow
                  leadingIcon="category"
                  label={t('myFarm.principalTypeLabel')}
                  value={principalTypeLabel(farm.principalType, t)}
                />
                <DetailRow
                  leadingIcon="groups"
                  label={t('myFarm.cooperative')}
                  value={farm.cooperativeName ?? t('myFarm.independent')}
                  muted={farm.cooperativeName == null}
                  noDivider
                />
              </Box>
            </CardContent>
          </Card>

          <Box>
            <Link to="/my-farm/edit" style={{ textDecoration: 'none' }}>
              <Button variant="contained">{t('myFarm.editButton')}</Button>
            </Link>
          </Box>
        </>
      )}
    </Stack>
  );
}
